import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Text,
  Spinner,
  Field,
  Input,
  Dropdown,
  Option,
  Button,
  Badge,
  MessageBar,
  MessageBarBody,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { SaveRegular } from '@fluentui/react-icons';
import { queryKeys } from '../lib/queryClient';
import {
  fetchEmailTemplates,
  saveEmailTemplate,
  EMAIL_TEMPLATE_TOKENS,
  EmailTemplate,
} from '../services/emailTemplateService';
import RichTextEditor from '../components/RichTextEditor';

const useStyles = makeStyles({
  page: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    minHeight: 0,
    flex: 1,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '760px',
  },
  layout: {
    display: 'flex',
    ...shorthands.gap('16px'),
    minHeight: 0,
    flex: 1,
    '@media (max-width: 768px)': {
      flexDirection: 'column',
    },
  },
  leftPanel: {
    width: '280px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  tokens: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('12px', '16px'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
  },
  tokenRow: {
    display: 'flex',
    flexWrap: 'wrap',
    ...shorthands.gap('8px'),
  },
  tokenLabel: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground2,
  },
  card: {
    flex: 1,
    minWidth: 0,
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('16px'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  cardHead: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
  },
  cardTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  bodyField: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    flex: 1,
  },
  cardFoot: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  fallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '240px',
    flex: 1,
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    color: tokens.colorNeutralForeground3,
  },
  savedText: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorText: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/** Local edit state for the selected template. */
interface Draft {
  subject: string;
  body: string;
}

function TemplateEditor({ template }: { template: EmailTemplate }) {
  const styles = useStyles();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Draft>({
    subject: template.subject,
    body: template.body,
  });
  const [saved, setSaved] = useState(false);

  // Reset the draft whenever the selected template changes.
  useEffect(() => {
    setDraft({ subject: template.subject, body: template.body });
    setSaved(false);
  }, [template.key, template.subject, template.body]);

  const mutation = useMutation({
    mutationFn: () =>
      saveEmailTemplate({ key: template.key, subject: draft.subject, body: draft.body }),
    onSuccess: () => {
      setSaved(true);
      // Invalidate by the ['email-templates'] prefix so BOTH this page's
      // no-order cache and the Send dialog's order-keyed caches refetch the
      // edited copy. queryKeys.emailTemplates.list(x) => ['email-templates', x].
      queryClient.invalidateQueries({ queryKey: ['email-templates'] });
    },
  });

  const dirty =
    draft.subject !== template.subject || draft.body !== template.body;

  return (
    <div className={styles.card}>
      <div className={styles.cardHead}>
        <Text className={styles.cardTitle}>{template.label}</Text>
        <Badge appearance="outline" color="informative" size="small">
          {template.key}
        </Badge>
      </div>

      <Field label="Subject">
        <Input
          size="large"
          value={draft.subject}
          onChange={(_, d) => {
            setDraft((p) => ({ ...p, subject: d.value }));
            setSaved(false);
          }}
        />
      </Field>

      <Field label="Body" className={styles.bodyField}>
        <RichTextEditor
          value={draft.body}
          onChange={(html) => {
            setDraft((p) => ({ ...p, body: html }));
            setSaved(false);
          }}
          minHeight={240}
        />
      </Field>

      <div className={styles.cardFoot}>
        <Button
          appearance="primary"
          icon={<SaveRegular />}
          disabled={!dirty || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        {saved && !dirty && <Text className={styles.savedText}>Saved</Text>}
        {mutation.isError && (
          <Text className={styles.errorText}>
            {(mutation.error as Error)?.message || 'Save failed'}
          </Text>
        )}
      </div>
    </div>
  );
}

export default function SettingsEmails() {
  const styles = useStyles();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'Email templates' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  // No order context → raw {token} template strings (with any DB overrides
  // already merged in by the backend). `null` keys the cache the same way the
  // Send dialog's no-order fetch does.
  const { data, isLoading, isError, error } = useQuery({
    queryKey: queryKeys.emailTemplates.list(null),
    queryFn: () => fetchEmailTemplates(null),
  });

  // `custom` is the blank dropdown option in the Send dialog — not editable here.
  const editable = useMemo(
    () => (data?.templates || []).filter((t) => t.key !== 'custom'),
    [data],
  );

  // Default to the first template once the catalog loads.
  useEffect(() => {
    if (!selectedKey && editable.length > 0) setSelectedKey(editable[0].key);
  }, [editable, selectedKey]);

  const selected = editable.find((t) => t.key === selectedKey) || null;

  return (
    <div className={styles.page}>
      <Text className={styles.description}>
        These are the prefill templates staff pick from in the order Send-Email
        dialog. Pick a template on the left, edit its subject and body on the
        right. Changes are saved to the database and take effect immediately for
        new sends.
      </Text>

      {isLoading && (
        <div className={styles.fallback}>
          <Spinner label="Loading templates…" />
        </div>
      )}

      {isError && (
        <MessageBar intent="error">
          <MessageBarBody>
            {(error as Error)?.message || 'Failed to load email templates.'}
          </MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !isError && (
        <div className={styles.layout}>
          <div className={styles.leftPanel}>
            <Field label="Template">
              <Dropdown
                size="large"
                placeholder="Select a template…"
                value={selected?.label || ''}
                selectedOptions={selectedKey ? [selectedKey] : []}
                onOptionSelect={(_, d) => setSelectedKey(d.optionValue as string)}
              >
                {editable.map((t) => (
                  <Option key={t.key} value={t.key}>
                    {t.label}
                  </Option>
                ))}
              </Dropdown>
            </Field>

            <div className={styles.tokens}>
              <Text className={styles.tokenLabel}>Available tokens</Text>
              <Text className={styles.description}>
                Drop any of these into a subject or body — they're replaced with
                the order's real values when an email is composed. Unknown or
                missing values render as empty.
              </Text>
              <div className={styles.tokenRow}>
                {EMAIL_TEMPLATE_TOKENS.map((t) => (
                  <Badge key={t} appearance="tint" color="brand" size="medium">
                    {t}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {selected ? (
            <TemplateEditor key={selected.key} template={selected} />
          ) : (
            <div className={styles.placeholder}>
              <Text>Select a template to edit.</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
